import React from "react"
import EventNoteIcon from "@material-ui/icons/EventNote"
import LabelIcon from "@material-ui/icons/Label"
import { Link } from "gatsby"
import { Typography, makeStyles, Box, Chip } from "@material-ui/core"
import Image from "gatsby-plugin-sanity-image"
import { TypographyProps } from "@material-ui/core/Typography"

const useStyles = makeStyles(theme => ({
  box: {
    position: "relative",
    border: theme.spacing(1),
    borderStyle: "solid",
    borderColor: theme.palette.primary.main,
    backgroundColor: ({ backgroundColor }: StyleProps) =>
      backgroundColor ?? theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    "&:hover": {
      "& $captions, & $tag": {
        opacity: 0.5,
      },
    },
  },

  title: {
    display: "flex",
    maxWidth: "90%",
    padding: theme.spacing(1),
    lineHeight: 1,
    backgroundColor: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  captions: {
    transition: theme.transitions.create("all", {
      easing: theme.transitions.easing.easeOut,
    }),
    width: "100%",
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    color: theme.palette.common.white,
    bottom: 8,
  },

  card: {
    transition: theme.transitions.create("all", {
      easing: theme.transitions.easing.easeOut,
    }),
    "&:hover": {
      transform: "scale(1.025)",
    },
    boxShadow: theme.shadows[6],
  },

  date: {
    display: "flex",
    flexDirection: "row",
    alignSelf: "flex-start",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
  },
  tag: {
    transition: theme.transitions.create("all", {
      easing: theme.transitions.easing.easeOut,
    }),
    position: "absolute",
    bottom: 8,
    right: 4,
  },

  chip: {
    height: "20px",
    backgroundColor: theme.palette.secondary.main,
    color: "white",
  },
  icon: {
    width: "15px",
    height: "15px",
    color: theme.palette.common.white,
  },
  eventIcon: {
    marginRight: "5px",
  },
  cardMedia: {
    height: 220,
    width: "100%",
  },
}))

interface StyleProps {
  backgroundColor: string
}

interface ImageCardProps {
  title: string
  titleVariant?: TypographyProps["variant"]
  img
  imgStyle?
  category?: string
  date?: string
  linkTo?: string
  backgroundColor?: string
  linkIsExternal?: boolean
}

export default function ImageCard({
  img,
  category,
  title,
  date,
  linkTo,
  titleVariant,
  imgStyle,
  backgroundColor,
  linkIsExternal,
}: ImageCardProps) {
  const classes = useStyles({ backgroundColor })
  return (
    <TypedLink isExternal={linkIsExternal} to={linkTo}>
      <Box className={classes.box}>
        <div className={classes.cardMedia}>
          {img && (
            <Image
              {...img}
              width={720}
              style={
                imgStyle ?? {
                  width: "100%",
                  height: "220px",
                  objectFit: "cover",
                }
              }
            />
          )}
        </div>
        <Box className={classes.captions}>
          <Typography
            variant={titleVariant ?? "h6"}
            component="div"
            className={classes.title}
          >
            {title}
          </Typography>
          {date && (
            <Box className={classes.date}>
              <EventNoteIcon className={classes.eventIcon} />
              <Typography variant="body2">{date}</Typography>
            </Box>
          )}
        </Box>
        {category && (
          <div className={classes.tag}>
            <Chip
              icon={<LabelIcon className={classes.icon} />}
              size="small"
              label={category}
              className={classes.chip}
            />
          </div>
        )}
      </Box>
    </TypedLink>
  )
}

interface TypedLinkProps {
  isExternal: boolean
  to: string
}
const TypedLink: React.FC<TypedLinkProps> = ({ children, isExternal, to }) => {
  return isExternal ? (
    <a href={to} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    <Link to={to}>{children}</Link>
  )
}
